/* tslint:disable:no-unused-variable */
/* tslint:disable:no-unsafe-any */
const primitives = ['string', 'boolean', 'double', 'integer', 'long', 'float', 'number', 'any', 'object'];
import { objectTypeAnnotation } from '@babel/types';
import * as moment from 'moment';

const hasOwnProperty = function(obj: any, ...keys: (string | symbol | number)[]) {
    for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            return true;
        }
    }
    return false;
};

const recordRegex: RegExp = /^{ \[\w+: (?<keyType>string|number)\]: (?<valueType>[a-zA-Z0-9]+); }$/;

export class ObjectSerializer {
    public constructor(
        private readonly enumsMap: { [index: string]: any },
        private readonly typeMap: {
            [index: string]: string | {
                discriminator: string | undefined;
                additionalProperties?: boolean | undefined;
                new (): any;
                getAttributeTypeMap(): Array<{ name: string; baseName: string; type: string }>;
            };
        }
    ) {}

    public findCorrectType(
            data: any,
            expectedType: string,
            loopEvaluation?: Set<string>
    ): string {
        if (data == undefined) {
            return expectedType;
        } else if (primitives.indexOf(expectedType.toLowerCase()) !== -1) {
            return expectedType;
        } else if (expectedType === 'Date') {
            return expectedType;
        } else {
            if (this.enumsMap[expectedType]) {
                return expectedType;
            }

            if (!this.typeMap[expectedType]) {
                return expectedType; // w/e we don't know the type
            }

            const mappedType = this.typeMap[expectedType];

            if (typeof mappedType === "string") {
                if (loopEvaluation === undefined) {
                    loopEvaluation = new Set<string>(expectedType);
                } else {
                    loopEvaluation.add(expectedType);
                }

                if (loopEvaluation.has(mappedType)) {
                    // Type is an alias loop.  We don't know the type.
                    return mappedType;
                }

                return this.findCorrectType(data, mappedType, loopEvaluation);
            }

            // Check the discriminator
            const discriminatorProperty = mappedType.discriminator;
            if (discriminatorProperty == null) {
                return expectedType; // the type does not have a discriminator. use it.
            } else {
                if (data[discriminatorProperty]) {
                    const discriminatorType = data[discriminatorProperty];
                    if (this.typeMap[discriminatorType]) {
                        return discriminatorType; // use the type given in the discriminator
                    } else {
                        return expectedType; // discriminator did not map to a type
                    }
                } else {
                    return expectedType; // discriminator was not present (or an empty string)
                }
            }
        }
    }

    public serialize(data: any, type: string) {
        let a = { type, data }; /*?*/

        if (data == null) {
            return data;
        }

        if (primitives.indexOf(type.toLowerCase()) !== -1) {
            return data;
        }

        if (type.lastIndexOf('Array<', 0) === 0) {
            const subType = type.substring(6, type.length - 1);
            const transformedData: any[] = [];

            for (const element of data) {
                transformedData.push(this.serialize(element /* ? */, subType /* ? */));
            }
            return transformedData;
        }

        if (type === 'Date') {
            if (typeof data === 'string') {
                if (data === '') {
                    return null;
                }
                return moment.utc(data).toJSON();
            }
            if (moment.isMoment(data)) {
                return data.toJSON();
            }
            return moment.utc(data).toJSON();
        }

        if (this.enumsMap[type]) {
            return data;
        }

        if (!this.typeMap[type]) {
            // look for types like: { [ key: string ]: IValueType }
            const theMatches: RegExpExecArray | null = recordRegex.exec(type);

            if (theMatches) {
                const instance: Record<string, unknown> = {};

                for (const [key, value] of Object.entries(data)) {
                    key; /*?*/
                    value; /*?*/
                    instance[key] = this.serialize(value, theMatches.groups!.valueType);
                }

                return instance;
            }

            // dont know the type
            return data;
        }

        // Get the actual type of this object
        type = this.findCorrectType(data, type);

        // get the map for the correct type.
        const typeMap = this.typeMap[type];

        if (typeof typeMap === "string") {
            // don't know the type.
            return data;
        }

        const attributeTypes = typeMap.getAttributeTypeMap();
        const instance: Record<string, unknown> = {};
        for (const attributeType of attributeTypes) {
            instance[attributeType.baseName] = this.serialize(getValuePascalOrCamelCase(data, attributeType.name), attributeType.type);
        }
        if (typeMap.additionalProperties) {
            for (const [key, value] of Object.entries(data)) {
                instance[key] = value;
            }
        }
        return instance;
    }

    public deserialize(data: any, type: string) {
        // polymorphism may change the actual type.
        type = this.findCorrectType(data, type);
        if (data == null) {
            if (type.lastIndexOf('Array<', 0) === 0) return [];
            return data;
        }
        if (type.lastIndexOf('Array<', 0) === 0) {
            const subType = type.substring(6, type.length - 1);
            const transformedData: any[] = [];
            for (const element of data) {
                transformedData.push(this.deserialize(element, subType));
            }

            // TODO: Validate these changes
            // const targetArray: unknown[] = [];
            // let transformedData: unknown[] | { Items: unknown[]; [key: string]: unknown } = targetArray;
            // let sourceArray: Array<unknown> | undefined;
            // if (Array.isArray(data)) {
            //     sourceArray = data;
            // } else if (Array.isArray(data.Items)) {
            //     const extraProps: Record<string, unknown> = {};

            //     Object.keys(data).forEach((key: string, index: number, array: string[]) => {
            //         if (key !== 'Items') {
            //             // We don't know the types on these properties, so we have
            //             // to pass them along as-is.
            //             extraProps[key] = data[key];
            //         }
            //     });
            //     transformedData = {
            //         Items: targetArray,
            //         ...extraProps,
            //     };
            //     sourceArray = data.Items;
            // }

            // if (sourceArray && sourceArray.length > 0) {
            //     sourceArray.forEach(element => {
            //         targetArray.push(this.deserialize(element, subType));
            //     });
            // }

            return transformedData;
        }
        if (primitives.indexOf(type.toLowerCase()) !== -1) {
            return data;
        }

        if (type === 'Date') {
            if (typeof data === 'string' && data === '') {
                return null;
            }
            return moment.utc(data);
        }

        if (this.enumsMap[type]) {
            // is Enum
            return data;
        }

        if (!this.typeMap[type]) {
            // look for types like: { [ key: string ]: IValueType }
            const theMatches: RegExpExecArray | null = recordRegex.exec(type);

            if (theMatches) {
                const instance: Record<string, unknown> = {};

                for (const [key, value] of Object.entries(data)) {
                    instance[key] = this.deserialize(value, theMatches.groups!.valueType);
                }

                return instance;
            }

            // dont know the type
            return data;
        }

        const typeMap = this.typeMap[type];

        if (typeof typeMap === "string") {
            // don't know the type.
            return data;
        }

        const instance: Record<string, unknown> = new typeMap();
        const attributeTypes = typeMap.getAttributeTypeMap();
        for (const attributeType of attributeTypes) {
            let attributeName: string = attributeType.name;

            if (this.doDeserialize(attributeType, data, attributeName, instance)) continue;

            // Let's check for the possible cases where the actual property name
            // doesn't match what OpenAPI told us it would be.  Since we're
            // passing this along to a client built off of the OpenAPI data,
            // our output will retain that casing, even though the original
            // response did not.
            if (this.doDeserialize(attributeType, data, attributeName.charAt(0).toUpperCase() + attributeName.slice(1), instance)) continue;
            if (this.doDeserialize(attributeType, data, attributeName.charAt(0).toLowerCase() + attributeName.slice(1), instance)) continue;
        }
        if (typeMap.additionalProperties) {
            for (const [key, value] of Object.entries(data)) {
                instance[key] = value;
            }
        }
        return instance;
    }

    private doDeserialize(
        attributeType: { name: string; baseName: string; type: string },
        data: any,
        attributeName: string,
        instance: any
    ) {
        if (hasOwnProperty(data, attributeName)) {
            instance[attributeType.name] = this.deserialize(getValuePascalOrCamelCase(data, attributeName), attributeType.type);
            return true;
        }
        return false;
    }
}

function getValuePascalOrCamelCase(data: Record<string, unknown>, key: string) {
    if (!data) return undefined;

    if (hasOwnProperty(data, key)) {
        return data[key];
    }

    key = key.charAt(0).toUpperCase() + key.slice(1);
    if (hasOwnProperty(data, key)) {
        return data[key];
    }

    key = key.charAt(0).toLowerCase() + key.slice(1);
    if (hasOwnProperty(data, key)) {
        return data[key];
    }

    return undefined;
}
